import {  useEffect, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useOidc } from '@/auth/OidcProvider'
import { setTokenProvider, api } from '@/lib/api'
import { SelectedOrgProvider } from '@/context/SelectedOrgContext'
import type { UserProfile } from '@/lib/apiTypes'
import { persistLastAuthError, clearLastAuthError, reportBootstrapError } from '@/lib/bootstrapError'
import { endJourney, setUser, trackEvent, trackTiming } from '@/lib/telemetry'
import { AppErrorScreen } from '@/app/AppErrorScreen'
import { WelcomeModal } from '@/app/WelcomeModal'
import '@/shared/styles/global.css'
import { AuthError } from '@/app/AuthError'
import { SigningOut, Spinner } from '@/app/Spinners'
import { MAX_AUTH_ATTEMPTS, bumpAuthAttempts, clearAuthAttempts, getAuthAttempts } from '@/app/authAttempts'
import { EventTracker, PageTracker } from '@/app/trackers'
import { hasSeenWelcome, markWelcomeSeen } from '@/app/welcomeSeen'

// Backoff between /api/auth/me bootstrap attempts. Two retries absorb a worker
// cold start or a single 5xx without blocking a healthy session.
export const ME_RETRY_DELAYS_MS = [500, 1500]

export function AuthGuard() {
  const { isAuthenticated, isLoading, isSigningOut, login, getAccessToken } = useOidc()
  const [registered, setRegistered] = useState(false)
  const [meError, setMeError] = useState<{ kind: 'unauthorized' | 'unavailable'; error: unknown } | null>(null)
  const [meRetryNonce, setMeRetryNonce] = useState(0)
  const [signinError, setSigninError] = useState<Error | null>(null)
  const [showWelcome, setShowWelcome] = useState(false)
  const loginStarted = useRef(false)

  useEffect(() => {
    setTokenProvider(getAccessToken)
  }, [getAccessToken])

  useEffect(() => {
    if (isAuthenticated) {
      clearAuthAttempts()
      clearLastAuthError()
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    let timer: number | undefined
    // §8: `auth.bootstrap` is a SPAN, not an event, because it has real duration - the token
    // exchange plus up to two retries of /api/auth/me with a 500/1500ms backoff. Time-to-usable-app
    // is the number worth measuring, and it is the one users actually feel.
    const bootstrapStartedAt = Date.now()
    const attempt = (retriesLeft: number) => {
      api.post<UserProfile>('/api/auth/me')
        .then((profile) => {
          if (!cancelled) {
            setRegistered(true)
            setMeError(null)
            // Identity for every subsequent browser span and log record. Deliberately from the
            // /api/auth/me RESPONSE rather than from the OIDC token: this `id` is the D1 users.id,
            // which is the exact value the Worker stamps as `user.id`, so a browser span and the
            // server span it caused name the same person with the same key. Reading the token's
            // `sub` instead would give a second identifier that joins to neither.
            //
            // Held in memory only and re-set on each load - see setUser's note on why an email is
            // never persisted to browser storage.
            setUser({ id: profile.user.id, email: profile.user.email })
            // The authentication journey COMPLETES here: registered flipped true, the app is usable.
            trackEvent('auth.signin.completed', undefined)
            trackTiming('auth.bootstrap', Date.now() - bootstrapStartedAt)
            endJourney('authentication')
          }
        })
        .catch((e: unknown) => {
          if (cancelled) return
          // A 401/403 means the worker rejected the token (IdP/audience/issuer
          // misconfig) - no point retrying. Token-acquisition failures (silent
          // renew rejected before the fetch even fired - no .status, but the
          // getAccessToken sentinel message or an oidc ErrorResponse .error
          // field) are equally terminal: retrying would only hammer the IdP,
          // and the remedy is a fresh sign-in, not "try the API again".
          // Anything else (network, 5xx) gets the bounded retries, then
          // blocks: every authenticated route needs the same worker
          // (/api/orgs et al), so proceeding would only smear one failure
          // across N per-page errors.
          const status = (e as { status?: number }).status
          const isTokenFailure =
            status === undefined &&
            ((e instanceof Error && e.message === 'No access token available') ||
              typeof (e as { error?: unknown }).error === 'string')
          if (status === 401 || status === 403 || isTokenFailure) {
            console.error('[auth.me] session rejected:', e)
            reportBootstrapError('auth-me-unauthorized')
            setMeError({ kind: 'unauthorized', error: e })
          } else if (retriesLeft > 0) {
            timer = window.setTimeout(
              () => attempt(retriesLeft - 1),
              ME_RETRY_DELAYS_MS[ME_RETRY_DELAYS_MS.length - retriesLeft],
            )
          } else {
            console.error('[auth.me] unreachable after retries:', e)
            reportBootstrapError('auth-me-unavailable')
            setMeError({ kind: 'unavailable', error: e })
          }
        })
    }
    attempt(ME_RETRY_DELAYS_MS.length)
    // Clearing the pending timer (not just flagging cancelled) matters: under
    // StrictMode's double-mount, or a Retry click, a zombie backoff chain would
    // otherwise fire attempts after cleanup.
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [isAuthenticated, meRetryNonce])

  useEffect(() => {
    if (isAuthenticated && !hasSeenWelcome()) setShowWelcome(true)
  }, [isAuthenticated])

  const dismissWelcome = () => { markWelcomeSeen(); setShowWelcome(false) }

  // signinRedirect() rejecting (OIDC discovery unreachable/404 - the exact
  // failure that blank-screened prod when the issuer slug changed) must render
  // something: this branch is checked first so a rejected redirect can't loop
  // back into login().
  const doLogin = () => {
    login().catch((e: unknown) => {
      console.error('[auth] signinRedirect failed:', e)
      persistLastAuthError(e, 'signin-redirect')
      reportBootstrapError('signin-redirect')
      setSigninError(e instanceof Error ? e : new Error(String(e)))
    })
  }

  // Trigger the sign-in redirect from an effect, not the render body: renders
  // can run without commit (StrictMode, concurrent rendering), which would
  // double-bump the breaker and race two signinRedirect calls. The ref makes
  // it once per mount; a failed round-trip remounts via the /callback replace.
  useEffect(() => {
    if (isLoading || isAuthenticated || signinError || loginStarted.current) return
    // A sign-out in flight has already removed the stored user, so this guard sees
    // an unauthenticated session. Starting a sign-in here would race the logout
    // navigation and could sign the user straight back in.
    if (isSigningOut) return
    if (getAuthAttempts() >= MAX_AUTH_ATTEMPTS) return
    loginStarted.current = true
    bumpAuthAttempts()
    doLogin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, isSigningOut, signinError])

  // Checked before every other branch: once sign-out starts, nothing else about
  // this session is worth rendering, and the alternative is the bare spinner.
  if (isSigningOut) return <SigningOut />

  if (signinError) {
    return (
      <AppErrorScreen
        message="Could not start sign-in."
        stage="signin-redirect"
        error={signinError}
        action={{ label: 'Retry', onClick: () => { clearAuthAttempts(); window.location.replace('/') } }}
      />
    )
  }

  if (isAuthenticated && meError) {
    if (meError.kind === 'unauthorized') {
      return (
        <AppErrorScreen
          title="Session not authorized"
          message="Signed in, but the API rejected your session. This usually clears on a fresh sign-in."
          stage="auth-me"
          error={meError.error}
          action={{ label: 'Sign in again', onClick: doLogin }}
        />
      )
    }
    return (
      <AppErrorScreen
        message="Signed in, but the TerraMantle API is unreachable."
        stage="auth-me"
        error={meError.error}
        action={{
          label: 'Retry',
          onClick: () => { setMeError(null); setMeRetryNonce((n) => n + 1) },
        }}
      />
    )
  }

  if (isLoading || (isAuthenticated && !registered)) return <Spinner />

  if (!isAuthenticated) {
    if (getAuthAttempts() >= MAX_AUTH_ATTEMPTS) return <AuthError />
    // The login effect above owns the redirect; render just holds the spinner.
    return <Spinner />
  }

  return (
    <SelectedOrgProvider>
      {/* Authenticated-session page + event tracking. Null-render, above
          the Outlet so useLocation sees every in-app route change. */}
      <PageTracker />
      <EventTracker />
      {/* The index route ('/') already renders the Dashboard, so "Get started" just
          dismisses - the user is dropped straight onto the landing page. */}
      {showWelcome && <WelcomeModal onClose={dismissWelcome} onGetStarted={dismissWelcome} />}
      <Outlet />
    </SelectedOrgProvider>
  )
}

